import React, { useEffect, useState } from 'react';
import debounce from 'lodash.debounce';
import StepperHeading from '../StepperHeading';
import {
	Box,
	Button,
	Dialog,
	Divider,
	Drawer,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { AppColors } from '../../../../Utils/helper/AppColors';
import { Checkbox, Spin, Tag } from 'antd';
import toast from 'react-hot-toast';
import { HelperTexts } from '../../../../Utils/helper/HelperTexts';
import CultService from '../../../../Services/CultService';
import DepartmentTag from '../../../../Components/DepartmentTag';
import FilterSelectionContainer from '../../../../Components/FilterSelectionContainer';
import { useSelector } from 'react-redux';
import SearchBar from '../../../../Components/SearchBar';
import { filterBySearch, getColorSettings } from '../../../../Utils/helper/commonUtils';
import UserThumbnail from '../../../../Components/User/UserThumbnail';
import CustomTablePagination from '../../../../Components/CustomTablePagination';
import PageLoader from '../../../../Components/PageLoader';
import DeleteConfirm from '../../../../Components/DeleteConfirm';
import Search from 'antd/es/transfer/search';
import { FilterListIcon } from '../../../../Components/Icons/Icons';
import CustomTag from '../../../../Components/CustomTag';
import { useLocation } from 'react-router-dom';
import DataNotFound from '../../../../Components/DataNotFound';

export const Filter = ({filterQuery , setFilterQuery , filterTypes = [] , buttonSize = 'small' }) => {
	const [anchor, setAnchor] = useState(false);
	const company = useSelector((state) => state?.app?.company);
	const [selected, setSelected] = useState({});
	const [filters, setFilters] = useState(null);
	const [initialFilters, setInitialFilters] = useState(null);
	const [categorySearches, setCategorySearches] = useState({}); // holds all searchs in key-value() pairs
	const [loading, setLoading] = useState(false);
	const [open, setOpen] = useState(false);

	const handleSelectFilter = (category, value) => {
		setSelected((prevFilters) => {
			const categoryValues = prevFilters[category] || [];
			if (categoryValues.includes(value)) {
				const updatedCategoryValues = categoryValues.filter((item) => item !== value);
				if (updatedCategoryValues.length === 0) {
					const { [category]: removed, ...rest } = prevFilters;
					return rest;
				}

				return {
					...prevFilters,
					[category]: updatedCategoryValues,
				};
			} else {
				return {
					...prevFilters,
					[category]: [...categoryValues, value],
				};
			}
		});
	};

	const filterCategory = (category, searchTerm) => {
		if (!initialFilters || !initialFilters[category]) return [];

		return initialFilters[category].filter((item) => item.toLowerCase().includes(searchTerm.toLowerCase()));
	};

	const handleCategorySearch = (category, searchTerm) => {
		setCategorySearches((prev) => ({ ...prev, [category]: searchTerm }));
		setFilters((prev) => ({
			...prev,
			[category]: filterCategory(category, searchTerm),
		}));
	};

	const handleApplyFilters = () => {
		setFilterQuery(selected);
		setAnchor(false);
	};

	const getFilters = async () => {
    try {
        let res = await CultService.getFilterValues({ params: { companyId: company?._id } });
        let allFilters = res?.data;
		allFilters = {
			...allFilters,
			genders : ['male', 'female', 'other']
		}

        if (filterTypes.length > 0) {
            // Include only the filters specified in filterTypes
            let filteredData = {};
            filterTypes.forEach((type) => {
                if (allFilters[type]) {
                    filteredData[type] = allFilters[type];
                }
            });
            setInitialFilters(filteredData);
            setFilters(filteredData);
        } else {
            // If filterTypes is empty, include all filters
            setInitialFilters(allFilters);
            setFilters(allFilters);
        }
    } catch (error) {
        console.error("Error fetching filters:", error);
    }
};
	// Reset only this filter's selection
	const handleResetFilter = (filter) => {
		setSelected((prev) => {
			const updated = { ...prev };
			delete updated[filter];
			return updated;
		});
	};

	// Select all items for this filter
	const handleSelectAllFilter = (filter) => {
		setSelected((prev) => ({
			...prev,
			[filter]: [...filters[filter]],
		}));
	};

	const handleResetCheck = () => {
		setOpen(true);
	};

	const handleReset = () => {
		setSelected({});
		setCategorySearches((prev) =>
			Object.keys(prev).reduce((acc, key) => {
				acc[key] = '';
				return acc;
			}, {}),
		);
		setOpen(false);
		setLoading(false);
	};

	const handleCancel = () => {
		setAnchor(false);
		setSelected(filterQuery); // revert to previous selection on cancel
	};

	useEffect(() => {
		if (initialFilters) {
			Object.keys(categorySearches).forEach((category) => {
				handleCategorySearch(category, categorySearches[category]);
			});
		}
	}, [initialFilters, JSON.stringify(categorySearches)]);

	// useEffect(() => {
	// 	if (Object.keys(selected).length === 0) {
	// 		handleApplyFilters();
	// 	}
	// }, [selected]);

	useEffect(() => {
		if (company?._id) getFilters();
	}, [company]);

	const localSettingColors = getColorSettings(); 


	return (
		<Stack>
			<Stack direction="row" alignItems="center" gap={1}>
				<Button
					onClick={(e) => setAnchor(e.currentTarget)}
					variant="contained"
					color='info'
					size={buttonSize || 'small'}
					startIcon={<FilterListIcon color={AppColors.WHITE} />}
				>
					Filters
				</Button>
			</Stack>

			<Dialog
				sx={{ zIndex: 9999 }}
				open={anchor}
				onClose={(event, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {return;}
                handleCancel();
                }}				maxWidth={false} // disables default max-width constraints
				PaperProps={{
					sx: {
						width: '90vw',
						height: '90vh',
						borderRadius: '10px',
					},
				}}
			>
				<FilterSelectionContainer
					title="users"
					onClose={() => { handleCancel(); }}
					handleApplyChanges={handleApplyFilters}
					handleResetCheck={handleResetCheck}
					resetDisable={( JSON.stringify(selected) !== JSON.stringify(filterQuery))}
				>
					<Stack gap={2} divider={<Divider />}>
						{Object?.keys(filters || {})?.map((filter, fIndex) => (
							<Stack
								key={fIndex}
								sx={{
									backgroundColor: '#ffffff',
									border: '1px solid #ECE7FC',
									padding: '20px',
									borderRadius: '5px',
								}}
								gap={2}
							>
								<Stack direction="row" alignItems="center" justifyContent="space-between">
									<Stack direction="row" alignItems="center" gap={2}>
										<Typography
											textTransform="capitalize"
											variant="subtitle1"
											sx={{
												fontFamily: 'Poppins',
												fontWeight: 600,
												fontSize: 18,
												lineHeight: '150%',
												color: '#1A1A1A',
											}}
										>
											{filter?.slice(0, -1)}
										</Typography>
										<SearchBar
											search={categorySearches[filter] || ''}
											setSearch={(value) => handleCategorySearch(filter, value)}
											color={ AppColors?.midnight_black }
						                    bgColor={AppColors?.lightest_grey }
											width={'100%'}
											variant={'small'}
											borderRadius={'5px'}
										/>
									</Stack>
									<Stack direction="row" gap={1}>
										<Button
											size="small"
											variant="text"
											sx={theme => ({
												color: theme.palette.primary.darker_primary,
												fontSize: 16,
												fontWeight: 600,
												lineHeight: '150%',
												fontFamily: 'Poppins',
												backgroundColor: 'transparent',
												boxShadow: 'none',
												textDecoration: 'none',
												'&:hover': {
													backgroundColor: 'transparent',
													color: theme.palette.primary.darker_primary,
													textDecoration: 'none',
													boxShadow: 'none',
												},
												cursor: 'pointer',
											})}
											onClick={() => handleResetFilter(filter)}
											disabled={!selected[filter]?.length}
										>
											Reset
										</Button>
										<Button
											size="small"
											variant="outlined"
											color="info"
											sx={theme => ({
												fontSize: 12,
												fontWeight: 600,
												lineHeight: '100%',
												fontFamily: 'Poppins',
												borderRadius: '5px',
												
											})}
											// sx={{ color: AppColors.DARKER_PRIMARY , fontSize: 12 , fontWeight: 600 , lineHeight : '100%' , fontFamily: 'Poppins' }}
											onClick={() => handleSelectAllFilter(filter)}
											disabled={selected[filter]?.length === filters[filter]?.length}
										>
											Select All
										</Button>
									</Stack>
								</Stack>

								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
									{filters?.[filter]?.map((item, iIndex) => (
										<Box
											key={iIndex}
											sx={theme => ({ backgroundColor: AppColors?.orange || theme.palette.primary.lightest_shade, alignItems: 'center', padding: '6px 10px', borderRadius: '5px' })}
										>
											<Stack key={iIndex} direction="row" alignItems="center" gap={1}>
												<Checkbox
													checked={selected[filter]?.includes(item) || false}
													onChange={() => handleSelectFilter(filter, item)}
													className="purple-checkbox"
												/>
												<Typography
													sx={theme => ({ fontFamily: 'Poppins', fontWeight: 400, fontSize: 14, lineHeight: '150%', color: AppColors?.white || theme.palette.primary.darker_primary })} // use theme for consistent styling
												>
													{item?.charAt(0)?.toUpperCase() + item?.slice(1)}
												</Typography>
											</Stack>
										</Box>
									))}
								</Box>
							</Stack>
						))}
					</Stack>
				</FilterSelectionContainer>
			</Dialog>

			<DeleteConfirm
				zIndex={99999}
				loading={loading}
				open={open}
				onDelete={handleReset}
				onClose={() => setOpen(false)}
				title="Reset Filter?"
				subTitle="Are you sure you want to reset your seettings?"
			/>
		</Stack>
	);
};

export default function CampaignUsers({ details, setDetails }) {
	const location = useLocation();

	const [list, setList] = useState([]);

	const companyId = useSelector((state) => state?.app?.company)?._id;
	const [filterQuery, setFilterQuery] = useState({});
	const [search, setSearch] = useState('');
	const [loading, setLoading] = useState(false);
	const [previousSearch, setPreviousSearch] = useState(''); // used for more optimes search(no un necessary api calls).
	const [pagination, setPagination] = useState({ count: 0, rowsPerPage: 10, page: 1 });

	const getList = async (page = 1, limit = pagination?.rowsPerPage) => {
		setLoading(true);
		try {
			let filters = filterQuery;
			if (search) {
				filters = { ...filterQuery, search: [search] };
			}
			let res;

			if (location?.state?.campaign?.type === '1' || location?.state?.campaign?.type === '2' || location?.state?.campaign?.type === '0' || location?.state?.campaign?.type === '3') {
				
				
				res = await CultService.getParticipatedNonParticipatedUsersPaginated({
					params: { page, limit, companyId, campaignId: location?.state?.campaign?._id, type: location?.state?.campaign?.type , filterQuery: filters },
				});
			} else {
				res = await CultService.getAllUsersPaginatedv1({ params: { page, limit, companyId, filterQuery: filters }  });
			}

			if (res.status == 200) {
				if (res?.data?.pagination) {
					const { totalUsers, currentPage, totalPages, pageSize } = res?.data?.pagination;
					setPagination({
						count: totalUsers,
						rowsPerPage: pageSize,
						totalPages: totalPages,
						page: currentPage,
					});
				}
				console.log('res--', res?.data);
				setList(res?.data?.users?.filter((o) => o?.isActive));
			}
		} catch (err) {
			// console.error('Error fetching users:', err);
			toast.error(HelperTexts.SOMETHING_WENT_WRONG);
		} finally {
			setLoading(false);
		}
	};

	const debouncegetList = debounce((searchTerm) => {
		setPreviousSearch(searchTerm);
		if (previousSearch === searchTerm) return;
		getList(1, pagination?.rowsPerPage);
	}, 1000);

	useEffect(() => {
		debouncegetList(search);

		return () => debouncegetList.cancel();
	}, [search]);

	console.log('details--', details);
	// console.log('list--', list);
	const handleSelectUser = (user) => {
		let users = details?.users || [];
		if (!users.includes(user._id)) {
			users.push(user._id);
		} else {
			users = users.filter((i) => i !== user._id);
		}
		setDetails({ ...details, users });
	};

	const handleAllSelect = () => {
		let selectedUsers = details?.users || [];

		if (selectedUsers.length === list.length) {
			setDetails({ ...details, users: [] });
		} else {
			setDetails({ ...details, users: list.map((i) => i._id) });
		}
	};

	useEffect(() => {
		if (companyId) getList();
	}, [JSON.stringify(filterQuery), companyId]);

	// const localSettingColors = getColorSettings(); 

	return (
		<> 
			<StepperHeading
				renderRight={
					<Stack direction="row" alignItems="center" paddingX={2} gap={2}>
						<SearchBar search={search} 
						color={ AppColors?.midnight_black }
						bgColor={AppColors?.lightest_grey }
						setSearch={setSearch} />
						<Filter filterQuery={filterQuery} setFilterQuery={setFilterQuery} />
					</Stack>
				}
				// title="Users"
				// subTitle={<>
				// 	Select the users for the campaign <span style={{ color: "red" }}>*</span>

				// 	</>}
			/>

			<Stack sx={{ paddingX: 2, paddingY: 1, overflow: 'hidden' }}>
				<TableContainer>
					{list?.length === 0 ? (
						<DataNotFound />
					) : (
						<Table stickyHeader>
							<TableHead
								sx={{
									'& .MuiTableCell-root': {
										fontSize: 12,
										color: AppColors.HEADING_TITLE2,
										fontWeight: 600,
										lineHeight: '115%',
										// paddingTop: 1,
										// paddingBottom: 1,
										padding: '8px',
									},
								}}
							>
								<TableCell>
									<Stack direction="row" alignItems="center" gap={1.5}>
										<Checkbox onClick={handleAllSelect} checked={details?.users?.length === list?.length} />
										Name
									</Stack>
								</TableCell>
								<TableCell>Email address</TableCell>
								<TableCell>Phone No.</TableCell>
								<TableCell>Teams</TableCell>
							</TableHead>
							<TableBody
								sx={{
									'& .MuiTableCell-root': { fontSize: 12, color: AppColors.midnight_black || AppColors.HEADING_TITLE4, fontWeight: 400, padding: '8px' },
								}}
							>
								{loading && (
									<TableRow>
										<TableCell colSpan={4} align="center">
											<Spin size="large" />
										</TableCell>
									</TableRow>
								)}
								{/* filterBySearch(list, search, ['firstName', 'lastName', 'email', 'phoneNumber']) */}
								{list?.map((c, index) => (
									<TableRow key={index} >
										<TableCell>
											<Stack direction="row" alignItems="center" gap={1.5}>
												<Checkbox checked={details?.users?.includes(c._id)} onClick={() => handleSelectUser(c)} size="small" />
												<UserThumbnail user={c} />
											</Stack>
										</TableCell>

										<TableCell>
											<Typography variant="subtitle2">{c?.email}</Typography>
										</TableCell>

										<TableCell>
											<Typography variant="subtitle2">{c?.phoneNumber}</Typography>
										</TableCell>
										<TableCell>{c?.department && <CustomTag tagTitle={c?.department} colorsInfo={{ "color": AppColors.midnight_black, "bgColor": AppColors.lightest_grey, "border": AppColors.light_grey }} />}</TableCell>
									</TableRow>
								))}
								<TableRow
									sx={{
										position: 'sticky',
										bottom: 0,
										backgroundColor: 'background.paper',
										zIndex: 2, // Ensure it's above other rows
									}}
								>
									<TableCell sx={{ padding: '6px' }} colSpan={6} align="center">
										<CustomTablePagination
											{...pagination}
											onChange={(p, r) => getList(p, r)}
											onRowsPerPageChange={(e) => getList(1, e.target.value)}
										/>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					)}
				</TableContainer>
			</Stack>
		</>
	);
}
